import { OneTrustCookieGroup } from '~/assets/constants/cookie-consent';

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: Array<Record<string, any>>;
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const disableConsent = useCookie<boolean>('disableConsent');

  const checkQuery = Boolean(
    nuxtApp._route.query?.disableConsent && nuxtApp._route.query.disableConsent === 'true'
  );

  // Do not load onetrust script (cookie/query based) if running pagespeed/lighthouse to debug performace bottlenecks
  if (disableConsent.value || checkQuery) return;

  const runtimeConfig = useRuntimeConfig();

  useScript(
    {
      key: 'one-trust',
      src: 'https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js',
      'data-domain-script': runtimeConfig.public.oneTrustDataDomainScript,
      'data-language': 'nl',
      type: 'text/javascript',
      crossorigin: 'anonymous',
      async: false,
      defer: false,
      fetchpriority: 'high',
    },
    {
      tagPriority: 'critical',
    }
  );

  /* As soon as user updates the consent, we process the selected preferences
  and store the accepted cookie categories in root store for application wide checks
  ----------------------------------------------------------------------------------
  */
  function manageUserConsent() {
    // Avoid processing cookie choices and re-updating on page reload
    window.dataLayer = window.dataLayer || [];

    // Store the original push method temporarily
    const originalPush = window.dataLayer.push;

    // Override the push method of dataLayer
    window.dataLayer.push = function (...args: Array<Record<string, any>>) {
      args.forEach((eventObj) => {
        if (eventObj && eventObj.event === 'OneTrustGroupsUpdated') {
          const eventGroups = eventObj.OnetrustActiveGroups?.split(',') ?? [];
          const activeGroups = eventGroups.filter((group: string) => group.trim() !== '');
          processConsentChange(activeGroups, nuxtApp);
        }
      });

      // Call the original push method to ensure dataLayer behavior remains intact
      return originalPush.apply(window.dataLayer, args);
    };
  }

  if (!import.meta.client) return;
  manageUserConsent();

  // Fire the virtual_page_view event on each page navigation only
  const router = useRouter();
  router.afterEach(async (to, from) => {
    // Integrate with external services like Datadog
    if (nuxtApp.$ddRum) (nuxtApp.$ddRum as any).startView({ name: to.name });

    // Avoid firing on the same page on refresh immediately to control delay
    if (to.fullPath === from.fullPath) return;
    await firePageViewEvent(nuxtApp, from.fullPath, to.fullPath);
  });
});

function processConsentChange(activeCookieGroups: OneTrustCookieGroup[], nuxtApp: any) {
  const hasConsentChoiceMade = computed(() => !!useCookie('OptanonAlertBoxClosed').value);

  if (!hasConsentChoiceMade.value) return;

  processQueuedEvents();

  controlDatadog(activeCookieGroups, nuxtApp);

  // verify user has accpted tracking cookies before loading exponea script @ref: app.vue >> useExponeaScript
  controlExponea(activeCookieGroups);
}

function controlDatadog(acceptedGroups: OneTrustCookieGroup[], nuxtApp: any) {
  const checkAnalyticsConsent = acceptedGroups.includes(OneTrustCookieGroup.Functional);
  if (checkAnalyticsConsent) {
    if (nuxtApp.$ddRum) (nuxtApp.$ddRum as any).setTrackingConsent('granted');
  }
}

function controlExponea(acceptedGroups: OneTrustCookieGroup[]) {
  const checkAnalyticsConsent = acceptedGroups.includes(OneTrustCookieGroup.Analytical);
  if (checkAnalyticsConsent) exponeaTrackingConsent.accept();
}

async function firePageViewEvent(nuxtApp: any, fromPath: string, toPath: string) {
  const { useRootStore } = await import('~/stores/root.store');
  const authStore = useAuthStore();
  const rootStore = useRootStore();
  // Log the virtual_page_view event
  logPageView({
    fromPath,
    toPath,
    isIncVat: rootStore.isIncVat,
    locale: nuxtApp.$i18n.locale.value,
    user: authStore.user,
  });
}
